// httpPorts — the Resource BC slice: lifecycle (add/create/rename/duplicate/
// delete/restore), the Source reads, and the Material load. The nine content-
// mutation primitives live in the `materialEditing` slice (same BC, split by
// size/cohesion); the agent-review reads live in the `proposal` slice.

import { Result } from "@bilda/utils";
import type { NewMaterial } from "../../components/MaterialCreate/MaterialCreate.js";
import { type ExternalError, mkMalformed } from "../externalError.js";
import { parseFileView } from "../fileAdapter.js";
import {
    getSourceOriginal,
    postUpload,
    sourceOriginalUrl,
} from "../httpClient.js";
import { parseShowResource } from "../materialAdapter.js";
import type { ResourcePorts } from "../ports/resource.js";
import type { PortResult } from "../ports/result.js";
import type {
    AddedSource,
    AddSourceFile,
    CreatedMaterial,
    DeletedResource,
    LoadedFile,
    LoadedMaterial,
    LoadedSource,
    MaterialId,
    ModulePartsChange,
    PartMoved,
    PromotedFile,
    RestoredSourceContent,
} from "../ports.js";
import { parseSourceContent } from "../sourceAdapter.js";
import { fields, list, str } from "../wireShape.js";
import { command } from "./shared.js";

// Ingest one author-supplied file as a Source by uploading its RAW bytes to
// `POST /uploads/source?filename=…` (no base64; the browser sets the media type
// from `File.type`) — workspace-scoped, no project id. The ok value is the
// SAME workflow output `{ type, resourceId, filename, contentHash,
// appendedCount }` as before (the transport moved, the envelope did not); the
// port maps `resourceId` → `sourceId`, mirrors the server's title derivation
// (basename of the returned filename — the wire carries no title field), and
// threads the `type` discriminant through unchanged (DB4: a byte-identical
// re-add is `reused_existing`, never a refusal). An unsupported/unreadable file
// is still a typed `ExternalError` (Rejected).

// Mirror of the server's `basename(filename)` title derivation, for both
// separator conventions — pure string surgery, no node:path in the browser.
const titleOfFilename = (filename: string): string => {
    const tail = filename.split("/").at(-1) ?? filename;
    return tail.split("\\").at(-1) ?? tail;
};

// The wire's `type` discriminant, narrowed to the two outcomes `AddedSource`
// distinguishes. Any OTHER/missing value degrades to `freshly_ingested` —
// the pre-DB4 shape had no `type` field at all, so this keeps an
// old-server/new-client pairing (or a malformed wire) from surfacing an
// unrecognised discriminant; it never happens against this codebase's own
// server, which always emits one of the two.
const addedSourceOutcome = (
    wireType: string | undefined,
): AddedSource["type"] =>
    wireType === "reused_existing" ? "reused_existing" : "freshly_ingested";

const addSource = async (file: AddSourceFile): PortResult<AddedSource> => {
    const r = await postUpload("/uploads/source", file.file, file.file.name);
    if (Result.isErr(r)) return r;
    const f = fields(r.value);
    const sourceId = str(f?.get("resourceId"));
    if (sourceId === undefined) {
        return Result.err(mkMalformed("addSource returned no resourceId"));
    }
    const filename = str(f?.get("filename")) ?? file.file.name;
    return Result.ok({
        type: addedSourceOutcome(str(f?.get("type"))),
        sourceId,
        title: titleOfFilename(filename),
    });
};

// Restore a source whose extract blob went missing by re-uploading the
// ORIGINAL file's RAW bytes (`POST /uploads/source/:id/content?filename=…`),
// addressed to an EXISTING source id — the server hashes the bytes and gates on
// the source's content hash. A hash mismatch (a different file) arrives as an
// `ok:false` envelope → typed `ExternalError` (Rejected), which the host
// surfaces as "upload the original".
const reuploadSourceContent = async (
    sourceId: string,
    file: AddSourceFile,
): PortResult<RestoredSourceContent> => {
    const r = await postUpload(
        `/uploads/source/${encodeURIComponent(sourceId)}/content`,
        file.file,
        file.file.name,
    );
    if (Result.isErr(r)) return r;
    // The unchanged source id (the re-upload restores the blob, never mints a
    // new resource); fall back to the requested id if the wire omits it.
    const restored = str(fields(r.value)?.get("sourceId")) ?? sourceId;
    return Result.ok({ sourceId: restored });
};

// Create one Material manually through the SAME primitive the agent's tooling
// uses: `POST /commands/createMaterial` with the picked template, the
// author's title, and (optionally) the source it derives from — workspace-
// scoped, no project id in the body, mirroring `addSource`. Content is
// omitted on purpose: the material lands with the template's canonical empty
// draft.

const createMaterial = async (
    material: NewMaterial,
): PortResult<CreatedMaterial> => {
    const r = await command("createMaterial", {
        templateId: material.templateId,
        title: material.title,
        ...(material.fromSourceId === undefined
            ? {}
            : { fromSourceId: material.fromSourceId }),
    });
    if (Result.isErr(r)) return r;
    const materialId = str(fields(r.value)?.get("materialId"));
    if (materialId === undefined) {
        return Result.err(mkMalformed("createMaterial returned no materialId"));
    }
    return Result.ok({ materialId });
};

// The module's id from a `putInNewModule` reply.
const moduleOf = (
    r: Result.Result<unknown, ExternalError>,
): Result.Result<CreatedMaterial, ExternalError> => {
    if (Result.isErr(r)) return r;
    const materialId = str(fields(r.value)?.get("module"));
    return materialId === undefined
        ? Result.err(mkMalformed("module command returned no module id"))
        : Result.ok({ materialId });
};

const putInNewModule = async (
    title: string,
    parts: readonly string[],
): PortResult<CreatedMaterial> =>
    moduleOf(await command("putInNewModule", { title, parts }));

const putInNewCourse = async (
    title: string,
    parts: readonly string[],
): PortResult<CreatedMaterial> =>
    moduleOf(await command("putInNewCourse", { title, parts }));

// The module and the parts that moved (`key`) from an add / remove reply.
const changeOf = (
    r: Result.Result<unknown, ExternalError>,
    key: "added" | "removed",
): Result.Result<ModulePartsChange, ExternalError> => {
    if (Result.isErr(r)) return r;
    const f = fields(r.value);
    const moduleId = str(f?.get("module"));
    return moduleId === undefined
        ? Result.err(mkMalformed("module command returned no module id"))
        : Result.ok({
              moduleId,
              parts: list(f?.get(key)).flatMap((p) => str(p) ?? []),
          });
};

const addToModule = async (
    moduleId: string,
    parts: readonly string[],
): PortResult<ModulePartsChange> =>
    changeOf(
        await command("addToModule", { module: moduleId, parts }),
        "added",
    );

const removeFromModule = async (
    moduleId: string,
    parts: readonly string[],
): PortResult<ModulePartsChange> =>
    changeOf(
        await command("removeFromModule", { module: moduleId, parts }),
        "removed",
    );

const reorderPart = async (
    moduleId: string,
    part: string,
    index: number,
): PortResult<PartMoved> => {
    const r = await command("reorderPart", { module: moduleId, part, index });
    if (Result.isErr(r)) return r;
    const f = fields(r.value);
    const from = f?.get("from");
    const to = f?.get("to");
    return typeof from === "number" && typeof to === "number"
        ? Result.ok({ moduleId, part, from, to })
        : Result.err(mkMalformed("reorderPart returned no places"));
};

// All three wrap existing commands. Rename and delete are hash-guarded, and
// the rail rows they serve carry no hash — so the port reads the CURRENT hash
// via `showResource` first, then issues the guarded command. The read-then-
// write pair is the same optimistic posture the editor takes (a concurrent
// mutation between the two refuses cleanly server-side). Delete returns the
// hash it used: the undo token `restoreResource` matches against the deleted
// wrapper's pre-image.

const currentResourceHash = async (
    resourceId: string,
): Promise<Result.Result<string, ExternalError>> => {
    const r = await command("showResource", { id: resourceId });
    if (Result.isErr(r)) return r;
    const resource = fields(
        fields(fields(r.value)?.get("view"))?.get("resource"),
    );
    const contentHash = str(resource?.get("contentHash"));
    if (contentHash === undefined) {
        return Result.err(mkMalformed("showResource returned no contentHash"));
    }
    return Result.ok(contentHash);
};

const renameResource = async (
    resourceId: string,
    title: string,
): PortResult<void> => {
    const rHash = await currentResourceHash(resourceId);
    if (Result.isErr(rHash)) return rHash;
    const r = await command("renameResource", {
        resourceId,
        title,
        expectedContentHash: rHash.value,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// Make an independent copy of a material through `duplicateResource`. The
// new id is minted server-side — workspace-scoped, mirroring `createMaterial`.
// The response carries the copy's `newResourceId` — the host opens it.
const duplicateResource = async (
    sourceId: string,
): PortResult<CreatedMaterial> => {
    const r = await command("duplicateResource", { sourceId });
    if (Result.isErr(r)) return r;
    const materialId = str(fields(r.value)?.get("newResourceId"));
    if (materialId === undefined) {
        return Result.err(
            mkMalformed("duplicateResource returned no newResourceId"),
        );
    }
    return Result.ok({ materialId });
};

const deleteResource = async (
    resourceId: string,
): PortResult<DeletedResource> => {
    const rHash = await currentResourceHash(resourceId);
    if (Result.isErr(rHash)) return rHash;
    const r = await command("deleteResource", {
        resourceId,
        expectedContentHash: rHash.value,
    });
    return Result.isErr(r) ? r : Result.ok({ contentHash: rHash.value });
};

const restoreResource = async (
    resourceId: string,
    priorContentHash: string,
): PortResult<void> => {
    const r = await command("restoreResource", {
        resourceId,
        expectedContentHash: priorContentHash,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

// Archive / unarchive (F86). Both are hash-guarded like delete; the rail
// rows carry no hash, so the port reads the CURRENT hash first, then issues
// the guarded command. Archive doesn't change the hash (a non-content
// mutation), so unarchive reads the (unchanged) live hash the same way.
const archiveResource = async (resourceId: string): PortResult<void> => {
    const rHash = await currentResourceHash(resourceId);
    if (Result.isErr(rHash)) return rHash;
    const r = await command("archiveResource", {
        resourceId,
        expectedContentHash: rHash.value,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const unarchiveResource = async (resourceId: string): PortResult<void> => {
    const rHash = await currentResourceHash(resourceId);
    if (Result.isErr(rHash)) return rHash;
    const r = await command("unarchiveResource", {
        resourceId,
        expectedContentHash: rHash.value,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

const loadMaterial = async (id: MaterialId): PortResult<LoadedMaterial> => {
    const r = await command("showResource", {
        id,
        expectedKind: "material",
    });
    if (Result.isErr(r)) return r;
    return parseShowResource(r.value);
};

// `showResource` with the source kind-guard, parsed by the pure boundary
// reader (`sourceAdapter`). A wrong-kind id refuses server-side and arrives as
// a typed `ExternalError` — never a mis-shaped view.
//
// Kind upgrade: the envelope carries no media type, so the port probes the
// original-bytes route and dispatches on its content-type. `application/pdf`
// upgrades to the `pdf` variant (page render + extracted-text toggle, kept on
// its bytes path — pdf.js needs the bytes). `image/*`, `audio/*`, `video/*`
// upgrade to the native `image`/`media` variants, which point a browser
// element straight at the `/original` URL (see `nativeUpgrade`). A `.docx`
// upgrades to `word` (its page render, on the bytes too). Anything else
// (other office formats, or an unknown type) falls through to the parsed
// text/unavailable projection unchanged. Probe failure or 404 also keeps that
// projection — degradation, never a failed load.
//
// KNOWN follow-up (double-fetch): `getSourceOriginal` already fetched the bytes
// here just to read the media type, and the native element then re-fetches from
// the URL. A lightweight HEAD/media-type probe, or carrying the media type in
// the `showResource` envelope, would remove the second read. Only `pdf` and
// `word` need the bytes this path fetched; image/media discard them for the URL.

const DOCX_MEDIA_TYPE =
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document";

// Pure media-type dispatch. Kept separate from the impure fetch above so the
// mapping (media-type → projection) reads as one total table. Only the two
// fall-through projections (`text`/`unavailable`) are ever upgraded — a richer
// projection (e.g. a synced video transcript) is left intact.
const nativeUpgrade = (
    source: LoadedSource,
    sourceId: string,
    mediaType: string,
    data: Uint8Array,
): LoadedSource => {
    if (
        source.content.type !== "text" &&
        source.content.type !== "unavailable"
    ) {
        return source;
    }
    const label = source.meta.filename;
    if (mediaType === "application/pdf") {
        return {
            ...source,
            meta: { ...source.meta, kind: "PDF" },
            content: {
                type: "pdf",
                data,
                blocks:
                    source.content.type === "text" ? source.content.blocks : [],
            },
        };
    }
    // A Word document (.docx only; the old binary .doc has no page render)
    // keeps its bytes for the page render, like a PDF (#89, E-06).
    if (mediaType === DOCX_MEDIA_TYPE) {
        return {
            ...source,
            content: {
                type: "word",
                data,
                blocks:
                    source.content.type === "text" ? source.content.blocks : [],
            },
        };
    }
    if (mediaType.startsWith("image/")) {
        return {
            ...source,
            content: { type: "image", src: sourceOriginalUrl(sourceId), label },
        };
    }
    if (mediaType.startsWith("audio/")) {
        return {
            ...source,
            content: {
                type: "media",
                kind: "audio",
                src: sourceOriginalUrl(sourceId),
                label,
            },
        };
    }
    if (mediaType.startsWith("video/")) {
        return {
            ...source,
            content: {
                type: "media",
                kind: "video",
                src: sourceOriginalUrl(sourceId),
                label,
            },
        };
    }
    return source;
};

const loadSource = async (sourceId: string): PortResult<LoadedSource> => {
    const r = await command("showResource", {
        id: sourceId,
        expectedKind: "source",
    });
    if (Result.isErr(r)) return r;
    const parsed = parseSourceContent(sourceId, r.value);
    if (Result.isErr(parsed)) return parsed;
    const source = parsed.value;
    const original = await getSourceOriginal(sourceId);
    if (original === null) return Result.ok(source);
    return Result.ok(
        nativeUpgrade(
            {
                ...source,
                original: {
                    mediaType: original.mediaType,
                    size: original.data.byteLength,
                    url: sourceOriginalUrl(sourceId),
                },
            },
            sourceId,
            original.mediaType,
            original.data,
        ),
    );
};

const loadFile = async (fileId: string): PortResult<LoadedFile> => {
    const r = await command("showResource", {
        id: fileId,
        expectedKind: "file",
    });
    return Result.isErr(r) ? r : parseFileView(r.value);
};

const promoteFileToSource = async (
    fileId: string,
    title: string,
    annotation: string,
): PortResult<PromotedFile> => {
    const r = await command("promoteFileToSourceManually", {
        fileId,
        title,
        annotation,
    });
    if (Result.isErr(r)) return r;
    const sourceId = str(fields(r.value)?.get("resourceId"));
    return sourceId === undefined
        ? Result.err(mkMalformed("promoteFileToSource returned no resourceId"))
        : Result.ok({ sourceId });
};

// Thin command wrappers; the host reloads the source view after each.

const saveSourceSummary = async (
    sourceId: string,
    summary: string,
): PortResult<void> => {
    const r = await command("updateSourceSummary", {
        resourceId: sourceId,
        summary,
    });
    return Result.isErr(r) ? r : Result.ok(undefined);
};

export const resourceHttp: ResourcePorts = {
    loadSource,
    loadFile,
    promoteFileToSource,
    saveSourceSummary,
    addSource,
    reuploadSourceContent,
    createMaterial,
    putInNewModule,
    putInNewCourse,
    addToModule,
    removeFromModule,
    reorderPart,
    renameResource,
    duplicateResource,
    deleteResource,
    restoreResource,
    archiveResource,
    unarchiveResource,
    loadMaterial,
};
